import { logger } from "@platform/browser-logger";
import { CellValueChangedEvent, GridApi } from "ag-grid-community";
import { RefObject } from "react";

import { commissionFields } from "../../../api/commissionInjection";
import { buildCommissionUpdateRequest } from "../../../api/commissionRequestBuilder";
import { RfpCoverage } from "../../../api/types";
import { NetworkBenefit, PlanEditorRowData } from "../types/values.types";
import { CrossCellContext } from "../validation/validators";

import {
  clearDependentCellValues,
  refreshCells,
  refreshDependentCells,
} from "./agGridUtils";
import { isGridDefault } from "./benchmarkUtils";
import { parsePlanField, qualifyFieldPath } from "./fieldPath";

import type { CommissionUpdateRequest } from "../../../api/commissionMutation";

// Helper types for dealing with arbitrary benefit values
type CellValues = string | number | boolean | null | undefined;

type UnknownBenefits = Record<string, unknown>;

// Args required to register the onCellValueChanged handler
interface RegisterOnCellValueChangedOptions {
  onCoverageChange?: (updatedCoverage: RfpCoverage) => void;
  onCommissionChange?: (request: CommissionUpdateRequest) => void;
  coverage?: RfpCoverage;
  getCurrentCoverage?: () => RfpCoverage | undefined;
  isInternalUpdateRef?: RefObject<boolean>;
  validateFieldFn?: (
    value: unknown,
    fieldOrCategory: string,
    field?: string,
    context?: CrossCellContext,
  ) => { isValid: boolean; failedValidators: string[] };
  onManuallyEdited?: (
    planId: string,
    fieldPath: string,
    options?: { isBenchmarkField?: boolean },
  ) => void;
  onAiFieldEdited?: (
    planId: string,
    fieldPath: string,
    newValue: unknown,
    oldValue: unknown,
  ) => void;
}

interface ValidateCellChangeParams {
  validateFieldFn: (
    value: unknown,
    fieldOrCategory: string,
    field?: string,
    context?: CrossCellContext,
  ) => { isValid: boolean; failedValidators: string[] };
  benefitKey: string;
  propertyKey?: string;
  coercedValue: CellValues;
  networkPath?: string;
  planIdentifier: string;
  data: PlanEditorRowData;
  api: GridApi;
  nodeId: string;
  columnId: string;
}

/**
 * Validates a cell value change and applies visual feedback for validation state.
 * Returns true if valid, false if invalid (blocks the save).
 */
const validateCellChange = ({
  validateFieldFn,
  benefitKey,
  propertyKey,
  coercedValue,
  networkPath,
  planIdentifier,
  data,
  api,
  nodeId,
  columnId,
}: ValidateCellChangeParams): boolean => {
  // Construct the full field key for validation lookup
  const fieldKey = propertyKey ? `${benefitKey}.${propertyKey}` : benefitKey;

  // Build context object with network and plan identifier
  const validationContext: CrossCellContext = {
    network: networkPath,
    planIdentifier,
  };

  const validationResult = validateFieldFn(
    coercedValue,
    fieldKey,
    undefined,
    validationContext,
  );

  if (!validationResult.isValid) {
    // Mark cell as invalid for visual feedback
    data._validationState = "invalid";

    // Refresh cell to apply validation-error styling
    refreshCells(api, [{ rowId: nodeId, field: columnId }]);

    return false;
  }

  // Clear validation state if it was previously invalid but now valid
  if (data._validationState === "invalid") {
    data._validationState = undefined;
    refreshCells(api, [{ rowId: nodeId, field: columnId }]);
  }

  return true;
};

/**
 * Type guard to check if benefits object has networkBenefits array.
 */
const hasNetworkBenefits = (
  benefits: unknown,
): benefits is { networkBenefits: NetworkBenefit[] } =>
  !!benefits &&
  typeof benefits === "object" &&
  "networkBenefits" in benefits &&
  Array.isArray(benefits.networkBenefits);

/**
 * Updates network benefit.
 * @param benefits Target benefits bucket
 * @param options Update details
 * @returns void
 */
const updateNetworkBenefit = (
  benefits: { networkBenefits?: NetworkBenefit[] },
  options: {
    benefitKey: string;
    propertyKey?: string;
    coercedValue: CellValues;
    networkPath: string;
    isFlattenedObjectField: boolean;
    objectStructure?: Record<string, unknown>;
  },
): void => {
  const {
    benefitKey,
    propertyKey,
    coercedValue,
    networkPath,
    isFlattenedObjectField,
    objectStructure,
  } = options;

  if (!benefits.networkBenefits) {
    benefits.networkBenefits = [] as NetworkBenefit[];
  }

  const networkBenefit = benefits.networkBenefits.find(
    (nb: NetworkBenefit) =>
      nb.network === networkPath || nb.networkName === networkPath,
  );

  if (networkBenefit) {
    if (isFlattenedObjectField) {
      const existingValue = networkBenefit[benefitKey] || objectStructure || {};
      networkBenefit[benefitKey] = {
        ...(typeof existingValue === "object" && existingValue !== null
          ? existingValue
          : {}),
        // TODO: XTRM-1084 - This assumes coercedValue is numeric for flattened object fields
        // Should validate/narrow the type based on the benefit field schema
        amount: coercedValue as number | null | undefined,
      };
    } else if (propertyKey) {
      if (!networkBenefit[benefitKey]) networkBenefit[benefitKey] = {};
      (networkBenefit[benefitKey] as UnknownBenefits)[propertyKey] =
        coercedValue;
    } else {
      networkBenefit[benefitKey] = coercedValue;
    }
  } else {
    // In some cases, we're trying to update networkBenefits when they haven't been initialized yet
    // Create them here if not found
    // TODO: XTRM-1084 - Type assertion needed for flattened object field amount assignment
    // Should validate/narrow the type based on the benefit field schema
    const newNetworkBenefit = {
      network: networkPath,
      [benefitKey]: isFlattenedObjectField
        ? {
            ...(objectStructure || {}),
            amount: coercedValue as number | null | undefined,
          }
        : propertyKey
          ? { [propertyKey]: coercedValue }
          : coercedValue,
    };
    benefits.networkBenefits.push(newNetworkBenefit as NetworkBenefit);
    logger.info(
      `[PlanEditor] Added new network benefit for network ${networkPath}:`,
      newNetworkBenefit,
    );
  }
};

/**
 * Helper function to set a benefit value of unknown structure.
 */
const setBenefitValue = (
  benefits: unknown,
  benefitKey: string,
  value: unknown,
): void => {
  (benefits as UnknownBenefits)[benefitKey] = value;
};

/**
 * Helper function to set a nested benefit using the `propertyKey` configuration
 */
const setBenefitPropertyValue = (
  benefits: unknown,
  benefitKey: string,
  propertyKey: string,
  value: unknown,
): void => {
  const bucket = ((benefits as UnknownBenefits)[benefitKey] ??
    {}) as UnknownBenefits;
  bucket[propertyKey] = value;
  (benefits as UnknownBenefits)[benefitKey] = bucket;
};

const isCommissionField = (field: string): boolean =>
  commissionFields.includes(field);

/**
 * Normalizes display-formatted numeric input (e.g., "50%", "$1,200")
 * into raw numbers for non-string/non-array fields.
 */
const normalizeFormattedNumericValue = (
  value: CellValues,
  cellDataType: unknown,
): CellValues => {
  if (
    cellDataType === "array" ||
    cellDataType === "string" ||
    typeof value !== "string"
  ) {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) return value;

  const numericCandidate = trimmed.replace(/[%,$\s]/g, "");
  if (!numericCandidate) return value;

  const numericValue = Number(numericCandidate);
  return Number.isNaN(numericValue) ? value : numericValue;
};

/**
 * If a fill-handle/clipboard operation passed a display label instead of the
 * stored enum value, reverse-map it to the canonical option value.
 */
export const normalizeLabelToValue = (
  value: CellValues,
  cell: Record<string, unknown>,
): CellValues => {
  if (typeof value !== "string") return value;
  const rawCellEditorParams = cell["cellEditorParams"];
  const cellEditorParams =
    rawCellEditorParams && !Array.isArray(rawCellEditorParams)
      ? (rawCellEditorParams as Record<string, unknown>)
      : undefined;
  const allowedValues = cellEditorParams?.["values"] as string[] | undefined;
  if (!Array.isArray(allowedValues) || allowedValues.includes(value))
    return value;

  const formatValue = cellEditorParams?.["formatValue"] as
    | ((v: string) => string)
    | undefined;
  const mappedValue = allowedValues.find((v) =>
    formatValue
      ? formatValue(v) === value
      : v.toLowerCase() === value.toLowerCase(),
  );

  if (mappedValue && mappedValue !== value) {
    logger.info("[PlanEditor] Remapped display label to canonical value", {
      from: value,
      to: mappedValue,
    });
  }

  return mappedValue ?? value;
};

/**
 * Normalizes incoming AG Grid values before persistence.
 * Handles formatted numeric strings (e.g. percentages) and dropdown labels.
 */
export const normalizeCellInputValue = (
  value: CellValues,
  cell: Record<string, unknown>,
): CellValues => {
  const normalizedNumeric = normalizeFormattedNumericValue(
    value,
    cell["dataType"],
  );
  return normalizeLabelToValue(normalizedNumeric, cell);
};

/**
 * Register the onCellValueChanged handler for the PlanEditor grid.
 */
export const registerOnCellValueChanged =
  ({
    onCoverageChange,
    onCommissionChange,
    coverage,
    getCurrentCoverage,
    isInternalUpdateRef,
    validateFieldFn,
    onManuallyEdited,
    onAiFieldEdited,
  }: RegisterOnCellValueChangedOptions) =>
  // TODO: reduce complexity further
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  (params: CellValueChangedEvent<PlanEditorRowData, CellValues>): void => {
    const { rowIndex, column, data, newValue, oldValue, colDef } = params;
    if (rowIndex == null || !data || !coverage || !coverage.benefitPlans)
      return;

    const nextRowIndex = rowIndex + 1;
    const colKey = column.getColId();

    const field = colDef.field;
    if (!field) {
      logger.warn(
        `[PlanEditor] Column definition missing field for colId ${colKey}`,
      );
      return;
    }

    const parsed = parsePlanField(field);
    if (!parsed) return;
    const { planId: planIdentifier, network: networkPath } = parsed;
    const benefitKey = data.benefitKey;
    const propertyKey = data.propertyKey;
    const cell = data.benefit.cell || {};
    const coercedValue = normalizeCellInputValue(newValue, cell);

    if (isCommissionField(benefitKey || "")) {
      if (!onCommissionChange || !coverage.rfpId) {
        return;
      }

      const latestCoverage = getCurrentCoverage?.() || coverage;

      if (!latestCoverage) {
        return;
      }

      const updatedCoverage = structuredClone(latestCoverage);

      const benefitPlan = updatedCoverage.benefitPlans.find(
        (plan) =>
          plan.identifier === planIdentifier || plan.id === planIdentifier,
      );

      if (!benefitPlan) {
        return;
      }

      if (!benefitPlan.benefits) {
        benefitPlan.benefits = {};
      }

      (benefitPlan.benefits as Record<string, unknown>)[benefitKey] =
        coercedValue;

      if (onCoverageChange) {
        if (isInternalUpdateRef) {
          isInternalUpdateRef.current = true;
        }
        // Track this plan as dirty so autosave only PATCHes it
        updatedCoverage._dirtyPlanIds = [
          ...(updatedCoverage._dirtyPlanIds ?? []),
          benefitPlan.id ?? planIdentifier,
        ];
        onCoverageChange(updatedCoverage);
      }

      const commissionData = buildCommissionUpdateRequest(
        planIdentifier,
        benefitKey,
        coercedValue,
        updatedCoverage,
      );

      if (!commissionData) {
        return;
      }

      onCommissionChange({
        rfpId: coverage.rfpId,
        benefitPlanId: planIdentifier,
        ...commissionData,
      });
      return;
    }

    // Run custom validation if validator function is provided
    if (validateFieldFn && benefitKey) {
      const isValid = validateCellChange({
        validateFieldFn,
        benefitKey,
        propertyKey,
        coercedValue,
        networkPath,
        planIdentifier,
        data,
        api: params.api,
        nodeId: params.node.id!,
        columnId: params.column.getColId(),
      });

      if (!isValid) {
        if (
          data.benefit?.revertOnInvalid &&
          oldValue !== null &&
          oldValue !== undefined
        ) {
          // Silently revert to previous value — no red border, no error toast
          data._validationState = undefined;
          refreshCells(params.api, [{ rowId: params.node.id!, field: colKey }]);
          params.node.setDataValue(colKey, oldValue);
        }
        return;
      }
    }

    // Get the latest coverage value from parent's ref (not the stale prop)
    // This is critical for rapid edits - without it, the second edit would
    // read from the old coverage prop and overwrite the first edit
    const latestCoverage = getCurrentCoverage?.() || coverage;

    if (!latestCoverage) {
      logger.warn("[PlanEditor] No coverage available to update");
      return;
    }

    // Create a deep copy of the latest coverage to avoid mutating shared references
    const updatedCoverage = structuredClone(latestCoverage);

    // Check if this is a coverage-level field (e.g., eligibilityClasses)
    const isCoverageLevelField = data.benefit?.coverageLevel === true;

    if (isCoverageLevelField) {
      // Update coverage-level field
      // TODO: is there a better way to type this?
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (updatedCoverage as any)[benefitKey] = coercedValue;

      // Coverage-level changes affect all plans, so mark every plan as dirty
      // to ensure they are all PATCHed on the next save.
      updatedCoverage._dirtyPlanIds = updatedCoverage.benefitPlans
        .map((plan) => plan.id)
        .filter((id): id is string => !!id);

      // Notify parent component that coverage has changed
      if (onCoverageChange) {
        if (isInternalUpdateRef) {
          isInternalUpdateRef.current = true;
        }
        onCoverageChange(updatedCoverage);
      }
      return;
    }

    // Find the benefit plan in the updated coverage copy
    const benefitPlan = updatedCoverage.benefitPlans.find(
      (plan) =>
        plan.identifier === planIdentifier || plan.id === planIdentifier,
    );

    if (!benefitPlan) {
      logger.warn(
        `[PlanEditor] Could not find benefit plan ${planIdentifier} to update`,
      );
      return;
    }

    // Check if this is a benefitPlan-level field (e.g., currentCarrierName)
    const isBenefitPlanLevelField = data.benefit?.benefitPlanLevel === true;
    if (isBenefitPlanLevelField) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (benefitPlan as any)[benefitKey] = coercedValue;

      // Mark as manually edited for benchmark-seeded plans
      if (onManuallyEdited && benefitKey) {
        const meta = (data as Record<string, unknown>)["meta"] as
          | { benchmarkSeededPlanIds?: Record<string, boolean> }
          | undefined;
        const resolvedPlanId = benefitPlan.id ?? planIdentifier;
        if (meta?.benchmarkSeededPlanIds?.[resolvedPlanId]) {
          const hadValue = !isGridDefault(params.oldValue, data.benefit);
          onManuallyEdited(resolvedPlanId, benefitKey, {
            isBenchmarkField: hadValue,
          });
        }
      }

      // Notify parent component that coverage has changed
      if (onCoverageChange) {
        if (isInternalUpdateRef) {
          isInternalUpdateRef.current = true;
        }
        // Track this plan as dirty so autosave only PATCHes it
        updatedCoverage._dirtyPlanIds = [
          ...(updatedCoverage._dirtyPlanIds ?? []),
          benefitPlan.id ?? planIdentifier,
        ];
        // Track the edited cell — useProposalPlanBenefitsMutation reads
        // _dirtyCells (not _dirtyPlanIds) to decide which plan-level fields
        // to include in the PATCH payload.
        if (benefitKey) {
          updatedCoverage._dirtyCells = [
            ...(updatedCoverage._dirtyCells ?? []),
            {
              planId: benefitPlan.id ?? planIdentifier,
              benefitKey,
              network: networkPath || undefined,
            },
          ];
        }
        onCoverageChange(updatedCoverage);
      }
      return;
    }

    // Initialize benefits to an empty object if missing — this happens for
    // carrier proposal plans that haven't had any benefits set yet (e.g. from
    // QAT where the API returns benefits: null). Without this, the edit is
    // silently dropped instead of writing the first benefit value.
    if (!benefitPlan.benefits) {
      benefitPlan.benefits = {};
    }

    const benefits = benefitPlan.benefits;

    const isFlattenedObjectField = data.benefit?.flattenedObjectField === true;
    const objectStructure = data.benefit?.objectStructure;

    const hasNetworksConfigured = data.benefit?.networkDependent && networkPath;

    if (hasNetworksConfigured) {
      updateNetworkBenefit(benefits as { networkBenefits?: NetworkBenefit[] }, {
        benefitKey,
        propertyKey,
        coercedValue,
        networkPath: networkPath!,
        isFlattenedObjectField,
        objectStructure,
      });
    } else {
      // Non-network benefit - update directly
      if (isFlattenedObjectField) {
        const existing = ((benefits as UnknownBenefits)[benefitKey] ??
          objectStructure ??
          {}) as UnknownBenefits;
        setBenefitValue(benefits, benefitKey, {
          ...existing,
          amount: coercedValue,
        });
      } else if (propertyKey) {
        setBenefitPropertyValue(
          benefits,
          benefitKey,
          propertyKey,
          coercedValue,
        );
      } else {
        setBenefitValue(benefits, benefitKey, coercedValue);
      }
    }

    // For typed-amount benefit pairs (e.g. cancer diagnosisBenefit / diagnosisBenefitAmount),
    // the Quotes tab collapses the amount row out of the grid and stores its per-column values
    // on the type row at `__collapsedSiblings[amountKey][columnField]`. When the user edits the
    // amount in the popup editor, `writeCollapsedSibling` mutates that map in-place. We read
    // it back here so the amount is included in the updatedCoverage sent to onCoverageChange
    // and ultimately PATCHed to the backend.
    const collapsedSiblings = (data as Record<string, unknown>)[
      "__collapsedSiblings"
    ] as Record<string, Record<string, unknown>> | undefined;
    if (collapsedSiblings && field) {
      for (const [siblingKey, columnValues] of Object.entries(
        collapsedSiblings,
      )) {
        if (Object.prototype.hasOwnProperty.call(columnValues, field)) {
          if (hasNetworksConfigured && hasNetworkBenefits(benefits)) {
            updateNetworkBenefit(benefits, {
              benefitKey: siblingKey,
              coercedValue: columnValues[field] as CellValues,
              networkPath: networkPath!,
              isFlattenedObjectField: false,
            });
          } else {
            setBenefitValue(
              benefits,
              siblingKey,
              columnValues[field] as CellValues,
            );
          }
        }
      }
    }

    // Handle dependent cells if this benefit declares dependencies
    if (data.benefit?.refreshesCells) {
      logger.info(`[PlanEditor] Handling dependent cells for ${benefitKey}`, {
        refreshesCells: data.benefit.refreshesCells,
        columnField: colKey,
      });

      // Get reset operations from clearDependentCellValues
      const resets = clearDependentCellValues(
        params.api,
        benefitKey,
        oldValue,
        coercedValue,
        data.benefit.refreshesCells,
        colKey,
      );

      // Capture old values of dependent cells before resets overwrite them
      const dependentOldValues = resets.map((reset) => {
        const rowNode = params.api.getRowNode(reset.benefitKey);
        return {
          value: rowNode?.data?.[colKey as keyof typeof rowNode.data],
          benefit: (rowNode?.data as PlanEditorRowData | undefined)?.benefit,
        };
      });

      // Apply resets to the coverage object in the same transaction
      // This prevents duplicate PATCH requests from setDataValue triggering onCellValueChanged
      resets.forEach((reset) => {
        if (hasNetworksConfigured && hasNetworkBenefits(benefits)) {
          updateNetworkBenefit(benefits, {
            benefitKey: reset.benefitKey,
            propertyKey: reset.propertyKey,
            coercedValue: reset.value as CellValues,
            networkPath: networkPath!,
            isFlattenedObjectField: false, // resets are plain values
          });
        } else {
          if (reset.propertyKey) {
            setBenefitPropertyValue(
              benefits,
              reset.benefitKey,
              reset.propertyKey,
              reset.value,
            );
          } else {
            setBenefitValue(benefits, reset.benefitKey, reset.value);
          }
        }
        // Also update the grid row data so the cell displays the reset value
        const rowNode = params.api.getRowNode(reset.benefitKey);
        if (rowNode && rowNode.data) {
          (rowNode.data as UnknownBenefits)[colKey] = reset.value;
        }
      });

      // Stamp resets as dirty cells too — otherwise a reset-only edit (e.g. an
      // FE-only toggle whose own key is excluded from the benefits PATCH) looks
      // like no benefit actually changed, and useProposalPlanBenefitsMutation's
      // hasBenefitEdit check skips the PATCH that would persist the reset.
      if (resets.length > 0) {
        updatedCoverage._dirtyCells = [
          ...(updatedCoverage._dirtyCells ?? []),
          ...resets.map((reset) => ({
            planId: benefitPlan.id ?? planIdentifier,
            benefitKey: reset.benefitKey,
            network: networkPath || undefined,
          })),
        ];
      }

      // Refresh the dependent cells to update their display/editability
      refreshDependentCells(
        params.api,
        colKey,
        data.benefit.refreshesCells,
        coercedValue,
      );

      // Cascaded resets (a dependent's own dependents, e.g. a type field's
      // amount field) aren't covered by refreshesCells above — refresh those too.
      refreshCells(
        params.api,
        resets.map((reset) => ({ rowId: reset.benefitKey, field: colKey })),
      );

      // Mark dependent cells as manually edited for benchmark-seeded plans.
      // When isBenchmarkField is true (old value was real data), shows edited pencil.
      // When isBenchmarkField is false (old value was empty/default), suppresses
      // the false-positive benchmark ribbon that would otherwise appear on the
      // new variant-default value.
      if (onManuallyEdited && resets.length > 0) {
        const meta = (data as Record<string, unknown>)["meta"] as
          | { benchmarkSeededPlanIds?: Record<string, boolean> }
          | undefined;
        const resolvedPlanId = benefitPlan?.id ?? planIdentifier;
        if (meta?.benchmarkSeededPlanIds?.[resolvedPlanId]) {
          resets.forEach((reset, idx) => {
            const oldVal = dependentOldValues[idx];
            const hadBenchmarkValue = !isGridDefault(
              oldVal.value,
              oldVal.benefit,
            );
            onManuallyEdited(
              resolvedPlanId,
              qualifyFieldPath(reset.benefitKey, networkPath),
              { isBenchmarkField: hadBenchmarkValue },
            );
          });
        }
      }
    }

    // Notify parent component that coverage has changed
    // IMPORTANT: The parent should NOT re-render with new coverage prop
    // or it will cause rowData to rebuild and lose pending changes
    if (onCoverageChange) {
      // Mark this as an internal update to prevent rowData rebuild
      if (isInternalUpdateRef) {
        isInternalUpdateRef.current = true;
      }
      // Track this plan as dirty so autosave only PATCHes it
      updatedCoverage._dirtyPlanIds = [
        ...(updatedCoverage._dirtyPlanIds ?? []),
        benefitPlan.id ?? planIdentifier,
      ];
      // Track the edited cell so the SBS view can persist the "Edited" notation
      // on the specific cell (and network sub-column) the analyst changed.
      if (benefitKey) {
        updatedCoverage._dirtyCells = [
          ...(updatedCoverage._dirtyCells ?? []),
          {
            planId: benefitPlan.id ?? planIdentifier,
            benefitKey,
            network: networkPath || undefined,
          },
        ];
      }
      onCoverageChange(updatedCoverage);
    }

    // Mark as manually edited if the cell has AI-extracted data,
    // or revert the flag if the value matches the AI-extracted original.
    if ((onManuallyEdited || onAiFieldEdited) && benefitKey) {
      const meta = (data as Record<string, unknown>)["meta"] as
        | {
            aiFieldLookup?: Record<string, unknown>;
            benchmarkSeededPlanIds?: Record<string, boolean>;
          }
        | undefined;
      const lookup = meta?.aiFieldLookup;
      if (lookup) {
        const networkSuffix = networkPath ? `::${networkPath}` : "";
        const aiKey = `${planIdentifier}::${benefitKey}${networkSuffix}`;
        const aiKeyBase = `${planIdentifier}::${benefitKey}`;
        const aiField = lookup[aiKey] || lookup[aiKeyBase];
        if (aiField) {
          onAiFieldEdited?.(
            benefitPlan?.id ?? planIdentifier,
            qualifyFieldPath(benefitKey, networkPath),
            coercedValue,
            oldValue,
          );
        }
      }

      // Mark as manually edited for benchmark-seeded plans
      const resolvedPlanId = benefitPlan?.id ?? planIdentifier;
      if (meta?.benchmarkSeededPlanIds?.[resolvedPlanId]) {
        const hadValue = !isGridDefault(params.oldValue, data.benefit);
        onManuallyEdited?.(
          resolvedPlanId,
          qualifyFieldPath(benefitKey, networkPath),
          { isBenchmarkField: hadValue },
        );
      }
    }

    if (nextRowIndex < params.api.getDisplayedRowCount()) {
      params.api.clearCellSelection();
      params.api.deselectAll();
      let nextColKey = colKey;
      params.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.rowIndex === nextRowIndex) {
          const isNetworkDependent = node.data?.benefit.networkDependent;
          if (isNetworkDependent) {
            return; // skip network dependent benefits
          }

          // not-network dependent fields are rendered as part of the first column group
          // with their colspan spanning the entire group
          // so we need to get the id of the first child column of the group
          const firstColumn = column.getParent()?.getChildren()?.[0];
          if (firstColumn) {
            nextColKey = firstColumn.getUniqueId();
          }
        }
      });
      params.api.setFocusedCell(nextRowIndex, nextColKey);
    }
  };
